<template>
    <div class="lms__superadmin_users_profile_edit_details">
        <template v-if="isEditMode && profileHasAPI">
            <component-note
                :class="'lms__superadmin_users_profile_edit_details_apinote'"
            >API is enabled for this workspace. User data is managed by external application now.</component-note>
        </template>
        <template v-else-if="isEditMode && userHasAPI">
            <component-note
                :class="'lms__superadmin_users_profile_edit_details_apinote'"
            >User's personal data is managed by external application and can be updated only through it. All their workspace settings can be updated still.</component-note>
        </template>
        <template v-if="isShowMode || isEditMode">
            <form-select
                v-show="false"
                :name="'user-profiles'"
                :label="'Profile'"
                :is-type="'select'"
                :value="profileId"
                :data-vv-as="'profile'"
                :get-items-getter="'getEbsUserProfilesMappedLabelValue'"
                :item-name="['profile', 'profiles']"
                :prevent-clear="true"
                @input="switchUserProfiles"
            />
        </template>
        <template v-if="user.isSuperadmin() && !loggedInUserTokenWorkspaceId">
            <form-select
                :name="'user-customer'"
                :disabled="isEditMode || isShowMode"
                :label="'Customer'"
                :is-type="'select'"
                :required="true"
                :value="profileCustomerId"
                :v-validate="'required'"
                :data-vv-as="'customer'"
                :get-pagination-getter="'getLmsSuperadminCustomersPagination'"
                :get-items-getter="'getLmsSuperadminCustomersMappedIdNameStatus'"
                :get-items-getter-mapped="'getLmsSuperadminCustomersMappedLabelValue'"
                :get-items-dispatch="'lmsSuperadminCustomersGet'"
                :item-name="['customer', 'customers']"
                :search-queryable="user.isSuperadmin()"
                :table-labels="customerTableLabels"
                :items-extra="profileCustomerName && profileCustomerId ? [
                    {
                        label: profileCustomerName,
                        value: profileCustomerId
                    }
                ] : []"
                :prevent-clear="true"
                @input="updateProfileCustomer"
                @input-table="updateProfileCustomer"
                @request-selected="updateProfileCustomer(...arguments, true)"
            />
        </template>
        <template v-else>
            <form-text
                :label="'Customer'"
                :name="'lms-superadmin-users-profile-edit-details-customer-readonly'"
                :read-only="true"
                :value="profileCustomerName"
            ></form-text>
        </template>
        <template v-if="!loggedInUserTokenWorkspaceId">
            <form-select
                :key="profileCustomerId"
                :disabled="isEditMode || isShowMode || !profileCustomerId"
                :name="'user-workspace'"
                :label="'Workspace'"
                :is-type="'select'"
                :required="true"
                :value="profileWorkspaceId"
                :v-validate="{ required: profileWorkspaceId ? false : true }"
                :data-vv-as="'workspace'"
                :get-items-dispatch="!user.isSuperadmin() && user.isMasterAdministrator() && profileCustomerId ? 'lmsSuperadminWorkspacesGet' : ''"
                :get-items-getter="'getLmsSuperadminWorkspacesMappedIdNameStatus'"
                :get-items-getter-mapped="'getLmsSuperadminWorkspacesMappedLabelValue'"
                :item-name="['workspace', 'workspaces']"
                :table-labels="[
                    {
                        key: 'name',
                        title: 'Name',
                        type: 'string',
                        sortable: true,
                        queryable: true
                    },
                    {
                        key: 'customer',
                        title: 'Customer',
                        type: 'string'
                    }
                ]"
                :items-extra="profileWorkspaceName && profileWorkspaceId ? [
                    {
                        label: profileWorkspaceName,
                        value: profileWorkspaceId
                    }
                ] : []"
                :prevent-clear="true"
                @input="updateProfileToWorkspace"
                @input-table="updateProfileToWorkspace"
            />
        </template>
        <template v-if="loggedInUserTokenWorkspaceId">
            <form-text
                :label="'Workspace'"
                :name="'lms-superadmin-users-profile-edit-details-workspace-readonly'"
                :read-only="true"
                :value="profileWorkspaceName"
            ></form-text>
        </template>
        <form-email
            :label="'Email'"
            :disabled="isShowMode || (isEditMode && userHasAPI) || (profileStatus !== '' && profileStatus !== 'active') || (!user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator() && user.isOrgUnitManager() && isEditMode)"
            :name="'user-email'"
            :required="true"
            :v-validate="'required|email'"
            :data-vv-as="'email'"
            v-model="profileEmail"
        ></form-email>
        <template v-if="isAddMode && (userProfileIsDeleted || userProfileIsExpired || userExists || Boolean(userProfileAlreadyExists))">
            <template v-if="userProfileIsDeleted || userProfileIsExpired">
                <component-note
                    :class="'lms__superadmin_users_profile_edit_details_existsnote'"
                    :link-active="true"
                    :link-text="`Yes, re-activate profile`"
                    @emit-link="openNotifyReactivateProfile"
                >This user already exists in our system and has an expired profile in this workspace. <br>Would you like to reactivate this profile?<br></component-note>
            </template>
            <template v-else-if="userExists || Boolean(userProfileAlreadyExists)">
                <component-note
                    :class="'lms__superadmin_users_profile_edit_details_existsnote'"
                    :link-active="Boolean(userProfileAlreadyExists) && (user.isSuperadmin() || user.isMasterAdministrator() || user.isUserAdministrator())"
                    :link-text="`Go to user's profile`"
                    :link-to="Boolean(userProfileAlreadyExists) ? { name: 'lms-superadmin-user-profile-edit', params: { userId: userProfileAlreadyExists.userId, profileId: userProfileAlreadyExists.id } } : {}"
                >{{ noteOutput }}
                </component-note>
            </template>
        </template>
        <template v-else>
            <form-text
                :label="'First name'"
                :disabled="disableUserFormFields || isShowMode || (isEditMode && userHasAPI) || (profileStatus !== '' && profileStatus !== 'active') || (!user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator() && user.isOrgUnitManager() && isEditMode)"
                :name="'user-first-name'"
                :required="true"
                :v-validate="'required|max:45'"
                :data-vv-as="'first name'"
                v-model="profileFirstName"
            />
            <form-text
                :label="'Last name'"
                :disabled="disableUserFormFields || isShowMode || (isEditMode && userHasAPI) || (profileStatus !== '' && profileStatus !== 'active') || (!user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator() && user.isOrgUnitManager() && isEditMode)"
                :name="'user-last-name'"
                :required="true"
                :v-validate="'required|max:45'"
                :data-vv-as="'last name'"
                v-model="profileLastName"
            />
        </template>
        <template v-if="rolesRequestPending || orgUnitsRequestPending || customFieldRequestPending">
            <component-loading></component-loading>
        </template>
        <template v-else>
            <form-select
                :class="profileHasAPI ? 'lms__superadmin_users_profile_edit_details_roles--apimode' : ''"
                :label="'Roles'"
                :disabled="isShowMode || Boolean(!customerRoles.length) || !profileWorkspaceId || (isEditMode && profileHasAPI) || profileStatus === 'deleted' || profileStatus === 'cancelled' || profileStatus === 'expired' || profileStatus === 'rejected' || (!user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator() && user.isOrgUnitManager())"
                :items="customerRoles"
                :required="true"
                :name="'user-roles'"
                :value="profileRoles"
                :items-extra="Boolean(profileCustomerId) ? [
                        {
                            label: 'Learner',
                            value: 'learner'
                        }
                    ] : []"
                :multiple="true"
                :v-validate="'required'"
                :data-vv-as="'roles'"
                :note-active="Boolean(profileCustomerId) && Boolean(!customerRoles.length)"
                :note-content="'There are no roles available to use.'"
                :field-active="Boolean(customerRoles.length) || !profileCustomerId"
                @input="updateProfileRoles"
            />
            <component-note
                :class="'lms__superadmin_users_profile_edit_details_emptyunits'"
                v-show="Boolean(profileWorkspaceId) && Boolean(!workspaceOrgUnits.length)"
            >There are no organisation units available to use.
            </component-note>
            <form-select-org-units
                v-show="Boolean(workspaceOrgUnits.length)"
                :required="user.isOrgUnitManager() && !user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator()"
                :v-validate="{ required: user.isOrgUnitManager() && !user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator() }"
                :data-vv-as="'organisation unit'"
                :label="'Organisation unit'"
                :name="'lms-superadmin-users-profile-edit-details-org-unit'"
                :disabled="isShowMode || Boolean(!workspaceOrgUnits.length) || (profileStatus !== '' && profileStatus !== 'active') || !profileWorkspaceId || (isEditMode && profileHasAPI)"
                :workspace-id="profileWorkspaceId"
                :value="profileUnit"
                :multiple="false"
                :check-parent-if-children-selected="false"
                :prevent-get-org-units="true"
                :allowed="user.isOrgUnitManager() && !user.isSuperadmin() && !user.isMasterAdministrator() && !user.isUserAdministrator() ? loggedInUserManagerUnits : null"
                @input="updateProfileUnits"
            >
                <template v-if="Boolean(workspaceOrgUnits.length) && profileCustomerId && profileWorkspaceId && isAddMode && user.isMasterAdministrator()">
                    Can't find units you're looking for?
                    <component-button
                        :name="'lms-superadmin-users-profile-edit-details-org-unit-viewunits'"
                        :text="true"
                        :size="'tiny'"
                        :underline="true"
                        :to="{ name: 'lms-superadmin-org-units-edit', query: { customerId: profileCustomerId, workspaceId: profileWorkspaceId} }"
                    >View units
                    </component-button>
                </template>
            </form-select-org-units>
            <component-custom-fields-handler
                :fieldset="workspaceCustomFields"
                :values="profileCustomFields"
                :disabled="(isEditMode && profileHasAPI) || profileStatus === 'deleted' || profileStatus === 'cancelled' || profileStatus === 'expired' || profileStatus === 'rejected'"
                @input="updateProfileCustomField"
            ></component-custom-fields-handler>
        </template>
        <form-date
            :label="'Expiry date'"
            :name="'user-expire-date'"
            :v-validate="{ date_format: 'yyyy-MM-dd', after: isEditMode && profileStatus === 'invited' ? yesterdaysDate : (profileStatus !== 'expired' ? yesterdaysDate : false) }"
            :v-validate-custom-error="`The expiry date must be after ${ isAddMode ? yesterdaysDate : (isEditMode && profileStatus === 'invited' ? yesterdaysDate : (profileStatus !== 'expired' ? yesterdaysDate : false)) }.`"
            :disabled="(isEditMode && profileHasAPI) || profileStatus === 'deleted' || profileStatus === 'cancelled' || profileStatus === 'expired' || profileStatus === 'rejected'"
            v-model="profileExpireDate"
        ></form-date>
        <form-select
            :disabled="(profileStatus !== '' && profileStatus !== 'active') || !profileWorkspaceId|| (isEditMode && profileHasAPI)"
            :data-vv-as="'groups'"
            :get-items-getter="'getLmsSuperadminGroupsMappedIdName'"
            :get-items-getter-mapped="'getLmsSuperadminGroupsMappedLabelValue'"
            :get-items-dispatch="profileWorkspaceId ? 'lmsSuperadminGroupsGet' : ''"
            :get-items-dispatch-data="profileWorkspaceId"
            :search-queryable="false"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                }
            ]"
            :item-name="['group', 'groups']"
            :label="'Groups'"
            :multiple="true"
            :name="`users-profile-edit-details-groups`"
            :value="profileWorkspaceGroups"
            :pop-table-filter-empty-state-title="'There are no groups in this workspace.'"
            @input="updateProfileWorkspaceGroups"
            @input-table="updateProfileWorkspaceGroups"
        ></form-select>
        <form-select
            ref="suggestedcoursesform"
            :disabled="(profileStatus !== '' && profileStatus !== 'active') || requestGetSuggestedCoursesPending || !profileWorkspaceId || (isEditMode && profileHasAPI)"
            :data-vv-as="'courses'"
            :get-pagination-getter="'getEbsCoursesPagination'"
            :get-items-getter="'getEbsCoursesMappedIdNameStatusLabels'"
            :get-items-getter-mapped="'getEbsCoursesMappedLabelValue'"
            :get-items-dispatch="profileWorkspaceId ? 'ebsCoursesGetAdmin' : ''"
            :get-items-dispatch-data="{ workspaceId: profileWorkspaceId }"
            :search-queryable="true"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                }
            ]"
            :item-name="['course', 'courses']"
            :label="'Enrol to courses'"
            :placeholder="'Search to add course'"
            :multiple="true"
            :name="`users-profile-edit-details-auto-enrolments`"
            :value="profileSuggestedCourses"
            :hint-position-top="true"
            @input="updateProfileSuggestedCourses"
            @input-table="updateProfileSuggestedCourses"
        >Below is a list of courses the user will be enrolled to. Add more by using the search bar below.
        </form-select>
        <template v-if="isShowMode || isEditMode">
            <component-wrapper>
                <div class="columns is-vcentered">
                    <div class="column"></div>
                    <div
                        v-if="profileStatus === 'invited' || profileStatus === 'rejected' || profileStatus === 'cancelled'"
                        class="column is-narrow"
                    >
                        <component-button
                            :name="'lms-superadmin-workspace-user-edit-form-resend'"
                            :theme="'default'"
                            @click="openNotifyResendInvitation"
                        >Resend invitation
                        </component-button>
                    </div>
                    <div
                        v-if="profileStatus === 'invited'"
                        class="column is-narrow"
                    >
                        <component-button
                            :name="'lms-superadmin-workspace-user-edit-form-cancel'"
                            :theme="'default'"
                            @click="openNotifyCancelInvitation"
                        >Cancel invitation
                        </component-button>
                    </div>
                </div>
            </component-wrapper>
        </template>
        <pop-notify
            v-if="profileStatus === 'invited'"
            :class="'pop__notify--cancel'"
            ref="notifycancelinvitation"
            :title-text="'Cancel invitation'"
            :content-text="`This user will not be able to accept the invite if you cancel the invitation. \n\nDo you wish to cancel the invitation?`"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Cancel invite'"
            :submit-button-danger="true"
            :submit-button-icon="true"
            :submit-button-icon-name="'x-circle'"
            :prevent-close="true"
            @emit-submit="cancelInvitation"
        />
        <pop-notify
            v-if="profileStatus === 'invited' || profileStatus === 'rejected' || profileStatus === 'cancelled'"
            :class="'pop__notify--resend'"
            ref="notifyresendinvitation"
            :title-text="'Resend invitation'"
            :content-text="'Are you sure want to resend this invitation?'"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Confirm'"
            :submit-button-danger="true"
            :prevent-close="true"
            @emit-submit="resendInvitation"
        />
        <pop-notify
            :class="'pop__notify--reactivate'"
            ref="notifyreactivateprofile"
            :title-text="'Re-activate profile?'"
            :content-text="`Re-activating this profile will allow the user to log back in to this workspace. \n\nDo you wish to reactivate this profile?`"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-text="'Re-activate profile'"
            :submit-button-icon="true"
            :submit-button-icon-name="'refresh-ccw'"
            :submit-button-icon-align="'right'"
            :prevent-close="true"
            @emit-submit="reactivateProfile"
        >
          <component-note v-if="userProfileIsExpired">The expiration date for this profile will be removed. You can assign a new expiry date by editing re-activated profile.</component-note>
        </pop-notify>
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import PopNotify from '@/components/default/shared/PopNotify'
import FormSelect from '@/components/default/forms/FormSelect'
import { validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit } from '@/helpers/mixins'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentNote from '@/components/default/shared/ComponentNote'
import { debounce, toISODateUTC } from '@/functions'
import { User } from '@/User'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import ComponentCustomFieldsHandler from '@/components/default/shared/ComponentCustomFieldsHandler'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'
import FormEmail from '@/components/default/forms/FormEmail'
import FormDate from '@/components/default/forms/FormDate'
import FormCheckbox from '@/components/default/forms/FormCheckbox'

export default {
  name: 'lms-superadmin-users-profile-edit-details',
  components: {
    FormCheckbox,
    FormDate,
    FormEmail,
    FormSelectOrgUnits,
    ComponentCustomFieldsHandler,
    ComponentLoading,
    ComponentWrapper,
    ComponentNote,
    ComponentButton,
    FormSelect,
    PopNotify,
    FormText
  },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  data () {
    return {
      disableUserFormFields: false,
      userExists: false,
      user: new User(),
      rolesRequestPending: false,
      orgUnitsRequestPending: false,
      requestGetSuggestedCoursesPending: false,
      customFieldRequestPending: false,
      preventUpdateSuggestedCourses: true
    }
  },
  computed: {
    todaysDate () {
      return toISODateUTC(new Date())
    },
    yesterdaysDate () {
      return toISODateUTC(new Date().getTime() - 1000 * 60 * 60 * 24)
    },
    loggedInUserManagerUnits () {
      return this.user.listOrgUnitManagerUnits()
    },
    loggedInUserTokenCustomerId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerId
    },
    loggedInUserTokenCustomerName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerName
    },
    loggedInUserTokenWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    loggedInUserTokenWorkspaceName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceName
    },
    loggedInUserProfilesWithMAorUARoles () {
      return this.$store.getters.getLmsSuperadminUserProfilesWithUAOrMARole
    },
    profileCustomerIdFromCustomerWithMARole () {
      const customers = this.$store.getters.getLmsSuperadminUserProfilesCustomersWithMARole
      return customers.findIndex(obj => obj.customerId === this.profileCustomerId) > -1
    },
    customerTableLabels () {
      let labelsArr = []

      labelsArr.push({
        key: 'name',
        title: 'Name',
        type: 'string',
        sortable: true,
        queryable: true
      })

      return labelsArr
    },
    customerRoles () {
      return this.$store.getters.getLmsSuperadminCustomerRoles
    },
    workspaceOrgUnits () {
      return this.$store.getters.getLmsSuperadminOrgUnitsMappedLabelValue
    },
    workspaceCustomFields () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldsData
    },
    routeQueryWorkspaceId () {
      return this.$route.query.workspaceId
    },
    routeQueryWorkspaceName () {
      return this.$route.query.workspaceName
    },
    routeQueryCustomerId () {
      return this.$route.query.customerId
    },
    routeQueryCustomerName () {
      return this.$route.query.customerName
    },
    //
    profileUserId () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileUserId
    },
    profileId () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileId
    },
    profileEmail: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileEmail
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetEmail', value)
      }
    },
    profileFirstName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileFirstName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetFirstName', value)
      }
    },
    profileLastName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileLastName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetLastName', value)
      }
    },
    profileStatus: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileStatus
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetStatus', value)
      }
    },
    profileExpireDate: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileExpireDate
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetExpireDate', value)
      }
    },
    profileIsUnitManager: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileIsUnitManager
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetIsUnitManager', value)
      }
    },
    profileRoles () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileRoles
    },
    profileWorkspaceId: {
      get () {
        if (this.isAddMode) {
          return this.$store.getters.getLmsSuperadminWorkspaceUserProfileToWorkspace
        } else {
          return this.$store.getters.getLmsSuperadminWorkspaceUserProfileWorkspaceId
        }
      }
    },
    profileWorkspaceName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileWorkspaceName
      }
    },
    profileUnit: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileUnit
      }
    },
    profileDefaultUnit: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserDefaultProfileUnit
      }
    },
    profileSuggestedCourses: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileSuggestedCourses
      }
    },
    profileWorkspaceGroups: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileWorkspaceGroups
      }
    },
    profileCustomerId: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileCustomerId
      }
    },
    profileCustomerName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileCustomerName
      }
    },
    profileInvitationId: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileInvitationId
      }
    },
    profileCustomFields: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceUserProfileCustomFields
      }
    },
    profileHasAPI () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileAPI
    },
    userHasAPI () {
      return this.$store.getters.getEbsUserHasAPI
    },
    //
    watchContent () {
      return this.profileCustomerId + this.profileWorkspaceId + this.profileEmail + this.profileFirstName + this.profileLastName + this.profileStatus + this.profileRoles
    },
    watchEmailDebounce () {
      return this.profileEmail + this.profileCustomerId + this.profileWorkspaceId
    },
    //
    isAddMode () {
      return this.$route.name.includes('user-profile-add')
    },
    isEditMode () {
      return this.$route.name.includes('user-profile-edit')
    },
    isShowMode () {
      return this.$route.name.includes('user-profile-show')
    },
    isStatusVisible () {
      return this.profileStatus === 'active' || this.profileStatus === 'inactive'
    },
    userAlreadyInvitedProfile () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileIsAlreadyInvited
    },
    userProfileAlreadyExists () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileAlreadyExists
    },
    userProfileIsDeleted () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileIsDeleted
    },
    userProfileIsExpired () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileIsExpired
    },
    noteOutput () {
      const userAlreadyExists = `This user already exists in our system but not in this workspace. \nYou can invite them to your workspace but you can't edit their name - users can update their own profile details if needed.`
      const userProfileAlreadyExists = 'This user already exists in our system and is a member of this workspace.'
      const notSelectedWorkspace = `This user already exists in our system but not in this workspace. You can invite them to your workspace but you can't edit their name - users can update their own profile details if needed.`
      const profileWorkspaceId = this.profileWorkspaceId
      return this.userProfileAlreadyExists ? userProfileAlreadyExists : (profileWorkspaceId ? userAlreadyExists : notSelectedWorkspace)
    }
  },
  watch: {
    watchEmailDebounce: debounce(function (value) {
      if (!value || this.isEditMode || this.isShowMode || !this.profileEmail) return
      this.updateProfileUnits('')
      this.updateProfileCombineSuggestedCourses([])
      this.$validator.reset()
      this.$validator.validate('user-email').then((result) => {
        if (result) {
          this.disableUserFormFields = true
          this.userExists = false
          this.$store.dispatch('lmsSuperadminWorkspaceUserProfileCheckEmail', this.profileEmail).then(() => {
            this.userExists = true
          }).catch(() => {
            this.disableUserFormFields = false
          })
        }
      })
    }, 800),
    profileCustomerId (value) {
      if (value) {
        this.getWorkspaceRoles()
      }
    },
    profileWorkspaceId (value) {
      if (value) {
        this.getOrgUnits(value)
        this.getWorkspaceCustomFields()
      }
    },
    profileUnit (value) {
      if (value) {
        this.handleUpdateCombineSuggestedCourses()
      }
    },
    profileWorkspaceGroups (value) {
      if (value) {
        this.handleUpdateCombineSuggestedCourses()
      }
    }
  },
  methods: {
    openNotifyReactivateProfile (data) {
      this.$refs.notifyreactivateprofile.setStoreValue(data)
      this.$refs.notifyreactivateprofile.openNotify()
    },
    reactivateProfile () {
      const profile = this.userProfileAlreadyExists
      const profileId = profile ? profile.id : false

      if (!profileId) return

      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileReactivate', profileId).then(() => {
        this.$eventBus.$emit('user-submit')
        this.$router.push({ name: 'lms-superadmin-user-list' })
        this.$store.dispatch('defaultNotificationToastsAdd', {
          title: 'Profile re-activated',
          message: ``,
          status: 'success'
        })
      }).finally(() => {
        this.$refs.notifyreactivateprofile.closeNotify()
      })
    },
    allowRedirectBeforeRouteLeave () {
      this.$eventBus.$emit('user-submit')
    },
    getWorkspaceRoles (data) {
      this.rolesRequestPending = true
      this.$store.dispatch('lmsSuperadminCustomerRolesGet', data || this.profileCustomerId).finally(() => {
        this.rolesRequestPending = false
      })
    },
    getOrgUnits (data) {
      this.orgUnitsRequestPending = true
      this.$store.dispatch('lmsSuperadminOrgUnitsGet', data || this.profileWorkspaceId).finally(() => {
        this.orgUnitsRequestPending = false
      })
    },
    getWorkspaceCustomFields (data) {
      this.customFieldRequestPending = true
      this.$store.dispatch('lmsSuperadminCustomFieldsGet', data || this.profileWorkspaceId).then(response => {
        this.customFieldRequestPending = false
      })
    },

    handleUpdateCombineSuggestedCourses () {
      if (this.profileStatus === 'active' && this.preventUpdateSuggestedCourses) return

      this.requestGetSuggestedCoursesPending = true
      this.updateProfileCombineSuggestedCourses([])

      const profileUnit = this.profileUnit
      const profileWorkspaceGroups = this.profileWorkspaceGroups
      const profileWorkspaceId = this.profileWorkspaceId
      let combineTargets = []

      if (profileUnit) {
        combineTargets.push(profileUnit)
      }
      if (profileWorkspaceGroups && profileWorkspaceGroups.length) {
        combineTargets = [...combineTargets, ...profileWorkspaceGroups]
      }

      if (combineTargets.length) {
        this.$store.dispatch('lmsUserAutoenrolmentsSuggestedCoursesGet', { targets: combineTargets, workspace: profileWorkspaceId }).then(response => {
          this.updateProfileCombineSuggestedCourses(response.map(obj => {
            return {
              value: obj.id
            }
          }))
          setTimeout(() => {
            this.$refs.suggestedcoursesform.performGetItems()
          }, 100)
        }).finally(() => {
          this.requestGetSuggestedCoursesPending = false
        })
      } else {
        this.requestGetSuggestedCoursesPending = false
      }
    },
    updateProfileRoles (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetRoles', data)
    },
    updateProfileToWorkspace (data) {
      this.updateProfileUnits('')
      this.updateProfileCombineSuggestedCourses([])
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetToWorkspace', data.value)
      this.updateProfileWorkspaceId(data.value)
      if (data.rawLabel || data.customLabel || data.label) {
        this.updateProfileWorkspaceName(data.rawLabel || data.customLabel || data.label)
      }
    },
    updateProfileWorkspaceId (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetWorkspaceId', data)
    },
    updateProfileWorkspaceName (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetWorkspaceName', data)
    },
    updateProfileCustomer (data, preventToWorkspace = false) {
      if (!data) return
      this.updateProfileCustomerId(data.value)
      this.updateProfileCustomerName(data.customLabel)
      this.$store.dispatch('lmsSuperadminWorkspacesSet', { data: data.extraData })
      if (!preventToWorkspace) {
        if (this.profileWorkspaceId) {
          this.updateProfileToWorkspace({ value: '', label: '' })
          this.updateProfileWorkspaceName('')
        }
      }
    },
    updateProfileUnits (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetUnit', data)
    },
    updateProfileWorkspaceGroups (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetWorkspaceGroups', data)
    },
    updateProfileCombineSuggestedCourses (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetCombineSuggestedCourses', data)
    },
    updateProfileSuggestedCourses (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetSuggestedCourses', data)
    },
    updateProfileCustomerId (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetCustomerId', data)
    },
    updateProfileCustomerName (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetCustomerName', data)
    },
    updateProfileCustomField (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileSetCustomField', data)
    },
    openNotifyCancelInvitation () {
      this.$refs.notifycancelinvitation.openNotify()
    },
    cancelInvitation () {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileCancelInvitation', { id: this.profileId, redirect: false }).then(() => {
        this.allowRedirectBeforeRouteLeave()
        this.$router.push({ name: 'lms-superadmin-user-list' })
      }).finally(() => {
        if (this.$refs.notifycancelinvitation) {
          this.$refs.notifycancelinvitation.closeNotify()
        }
      })
    },
    openNotifyResendInvitation () {
      this.$refs.notifyresendinvitation.openNotify()
    },
    resendInvitation () {
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileResendInvitation', { id: this.profileId, redirect: false }).then(() => {
        this.allowRedirectBeforeRouteLeave()
        this.$router.push({ name: 'lms-superadmin-user-list' })
      }).finally(() => {
        if (this.$refs.notifyresendinvitation) {
          this.$refs.notifyresendinvitation.closeNotify()
        }
      })
    },
    switchUserProfiles (data) {
      const profileUserId = this.profileUserId
      const profileId = data.value
      this.$router.push({ name: 'lms-superadmin-user-profile-edit', params: { userId: profileUserId, profileId: profileId } })
    },
    handlePreventUpdateSuggestedCourses () {
      setTimeout(() => {
        this.preventUpdateSuggestedCourses = false
      }, 100)
    }
  },
  mounted () {
    this.$eventBus.$on('user-profile-ready', this.handlePreventUpdateSuggestedCourses)

    if (this.profileCustomerId) {
      this.getWorkspaceRoles()
    }

    if (
      this.isAddMode
    ) {
      if (this.loggedInUserTokenWorkspaceId) {
        this.updateProfileCustomerId(this.loggedInUserTokenCustomerId)
        this.updateProfileCustomerName(this.loggedInUserTokenCustomerName)
        this.updateProfileToWorkspace({ value: this.loggedInUserTokenWorkspaceId, label: this.loggedInUserTokenWorkspaceName })
      } else {
        if (!this.user.isSuperadmin()) {
          this.updateProfileCustomerId(this.loggedInUserTokenCustomerId)
          this.updateProfileCustomerName(this.loggedInUserTokenCustomerName)
        }
      }
    }
  },
  beforeDestroy () {
    this.$eventBus.$off('user-profile-ready')
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/users/_lmssuperadminusersprofileeditdetails.scss";
</style>
